<script setup lang="ts">
import { computed } from 'vue'
import { ElLink } from 'element-plus'
import { useRouter, type RouteLocationRaw } from 'vue-router'

/**
 * A real anchor, in the app or out of it: `to` for an in-app destination the router
 * resolves, `href` for an external address (give those `target="_blank"`).
 *
 * Either way it is an `<a>`, so a middle-click or ⌘-click opens a new tab — which is why a
 * link is not a `Button` with a click handler. Inside a line of prose pass `strong`.
 */
const props = defineProps<{
  type?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default'
  href?: string
  /**
   * An in-app destination, resolved through the router — the same thing `RouterLink`
   * takes. Prefer it over spelling a `#/…` href by hand: the route definitions stay
   * the one place a path is written, and the anchor is still a real one, so the app
   * navigates on click and the link opens in a new tab like any other.
   */
  to?: RouteLocationRaw
  target?: string
  underline?: boolean
  disabled?: boolean
  strong?: boolean
}>()

const router = useRouter()

/**
 * `router.resolve` throws on a named destination whose params are incomplete — a row
 * whose id has not arrived yet is enough. A link that cannot say where it goes is
 * rendered inert rather than taking the surrounding page down with it; the missing
 * param is the caller's bug to fix, not a reason for the view to stop rendering.
 */
const resolvedHref = computed(() => {
  if (!props.to) return props.href

  try {
    return router.resolve(props.to).href
  } catch {
    return undefined
  }
})

const isInert = computed(() => !!props.to && !resolvedHref.value)
</script>

<template>
  <ElLink
    :class="{ 'ui-link--strong': strong }"
    :type="type"
    :href="resolvedHref"
    :disabled="disabled || isInert"
    :target="target"
    :underline="underline"
    v-bind="$attrs"
  >
    <slot />
  </ElLink>
</template>

<style lang="scss" scoped>
.ui-link--strong {
  vertical-align: baseline;
  font-weight: 600;
}
</style>
